import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";

const toDatabaseSuffix = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Failed to create the client databases.";

export default function AddClientDialog({
  open,
  onClose,
  tableSets,
  disciplines,
  onCreate,
}) {
  const [clientName, setClientName] = useState("");
  const [dbSuffix, setDbSuffix] = useState("");
  const [suffixEdited, setSuffixEdited] = useState(false);
  const [templateTableSet, setTemplateTableSet] = useState("general");
  const [creating, setCreating] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;

    setClientName("");
    setDbSuffix("");
    setSuffixEdited(false);
    setTemplateTableSet("general");
    setCreating(false);
    setProgressMessage("");
    setError("");
  }, [disciplines, open]);

  const existingLabels = useMemo(
    () => new Set(tableSets.map((item) => item.label.trim().toLowerCase())),
    [tableSets]
  );

  const existingSuffixes = useMemo(
    () =>
      new Set(
        tableSets
          .filter((item) => item.type === "client")
          .map((item) => item.dbSuffix.trim().toLowerCase())
      ),
    [tableSets]
  );

  const handleNameChange = (event) => {
    const value = event.target.value;
    setClientName(value);
    if (!suffixEdited) setDbSuffix(toDatabaseSuffix(value));
    setError("");
  };

  const handleSuffixChange = (event) => {
    setSuffixEdited(true);
    setDbSuffix(toDatabaseSuffix(event.target.value));
    setError("");
  };

  const handleCreate = async () => {
    const label = clientName.trim();
    const suffix = dbSuffix.trim().toLowerCase();

    if (!label) {
      setError("Enter a client name.");
      return;
    }
    if (!suffix) {
      setError("Enter a valid database suffix.");
      return;
    }
    if (!/^[a-z0-9]+(?:_[a-z0-9]+)*$/.test(suffix)) {
      setError("Use only lowercase letters, numbers, and underscores in the suffix.");
      return;
    }
    if (existingLabels.has(label.toLowerCase())) {
      setError(`Client "${label}" already exists.`);
      return;
    }
    if (existingSuffixes.has(suffix)) {
      setError(`Database suffix "${suffix}" is already in use.`);
      return;
    }
    if (!tableSets.some((item) => item.value === templateTableSet)) {
      setError("Choose a valid template table set.");
      return;
    }
    try {
      setCreating(true);
      setError("");
      setProgressMessage("Checking the template databases...");

      await onCreate(
        {
          label,
          value: suffix,
          dbSuffix: suffix,
          templateTableSet,
          disciplineValues: disciplines.map((item) => item.value),
        },
        setProgressMessage
      );

      onClose();
    } catch (creationError) {
      console.error("Failed to create client:", creationError);
      setError(getErrorMessage(creationError));
      setProgressMessage("");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={creating ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ fontWeight: 600 }}>Add new client</DialogTitle>

      <DialogContent sx={{ pt: "14px !important" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Alert severity="info">
            The selected database schemas will be copied without any data rows.
          </Alert>

          <TextField
            label="Client name"
            size="small"
            fullWidth
            value={clientName}
            onChange={handleNameChange}
            disabled={creating}
            autoFocus
          />

          <TextField
            label="Database suffix"
            size="small"
            fullWidth
            value={dbSuffix}
            onChange={handleSuffixChange}
            disabled={creating}
            helperText="Example: ABC Engineering becomes piping_abc_engineering."
          />

          <FormControl fullWidth size="small" disabled={creating}>
            <InputLabel>Copy schemas from</InputLabel>
            <Select
              label="Copy schemas from"
              value={templateTableSet}
              onChange={(event) => {
                setTemplateTableSet(event.target.value);
                setError("");
              }}
            >
              {tableSets.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl component="fieldset" variant="standard" disabled={creating}>
            <FormLabel component="legend">Discipline databases</FormLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {disciplines.map((item) => item.label).join(", ")}
            </Typography>
          </FormControl>

          {creating && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {progressMessage || "Creating client databases..."}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="secondary" onClick={onClose} disabled={creating}>
          Cancel
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={handleCreate}
          disabled={creating}
        >
          {creating ? "Creating..." : "Create client"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
